import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import { MultipleSelectFilter } from "~components/MultipleSelectFilter/MultipleSelectFilter.tsx";
import { AgeGroups } from "~enums/ageGroups.ts";
import { TableFilter } from "~enums/tableFilter.ts";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope.ts";
import { CompetitionFilterTypes } from "~pages/Competitions/CompetitionsListPage/enums/CompetitionFilterTypes.ts";

export const useGetCompetitionsTableFilters = (): {
  filters: DataTableFilter[];
  currentFilter: Record<string, any>;
  handleFilterChange: (filterKey: string, values: any) => void;
} => {
  const [currentFilter, setCurrentFilter] = useState<any>({});
  const { data: scopeData } = useGetCurrentUserPermissionScope();
  const { resources } = scopeData || {};
  const { formatMessage } = useIntl();

  const leaguesData = resources?.leagues || {};

  const transformedLeagues = Object.values(leaguesData).map((league) => ({
    label: league.nameLocal,
    value: league.leagueId,
  }));

  const ageGroupOptions = Object.values(AgeGroups).map((value) => ({
    value,
    label: formatMessage({ id: `ageGroup.${value}` }),
  }));

  const handleFilterChange = useCallback((filterKey: string, values: any) => {
    setCurrentFilter((previousFilter: Record<string, any>) => ({
      ...previousFilter,
      [filterKey]: values,
    }));
  }, []);

  const filters = [
    {
      filterKey: CompetitionFilterTypes.AgeGroup,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={CompetitionFilterTypes.AgeGroup}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "competitionsListPage.filters.ageGroup.allOption",
          })}
          options={ageGroupOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: CompetitionFilterTypes.League,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={CompetitionFilterTypes.League}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "competitionsListPage.filters.leagues.allOption",
          })}
          options={transformedLeagues}
          onChange={props.onChange}
        />
      ),
    },
  ];

  return { filters, currentFilter, handleFilterChange };
};
